import { SaxesParser } from 'saxes';

export const WORD_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
export const STRICT_WORD_NS = 'http://purl.oclc.org/ooxml/wordprocessingml/main';
export const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
export const STRICT_REL_NS = 'http://purl.oclc.org/ooxml/officeDocument/relationships';
export const PACKAGE_REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';

export interface XmlNode {
  name: string;
  local: string;
  uri: string;
  attributes: Record<string, string>;
  children: XmlNode[];
  text: string;
}

export class XmlFault extends Error {
  constructor(readonly code: string, readonly location?: string) {
    // Parser messages can contain document text. Only bounded structural evidence escapes.
    super(code);
  }
}

export function decodeXml(bytes: Uint8Array): string {
  const encoding = bytes[0] === 0xff && bytes[1] === 0xfe ? 'utf-16le'
    : bytes[0] === 0xfe && bytes[1] === 0xff ? 'utf-16be' : 'utf-8';
  return new TextDecoder(encoding, { fatal: true }).decode(bytes);
}

/** Strict XML and namespace parsing, not an OOXML XSD validator. */
export function parseXml(xml: string): XmlNode {
  // Source: https://github.com/lddubeau/saxes (verified 2026-09-16).
  // Do not recover after an error: saxes' later events are no longer evidence.
  const parser = new SaxesParser({ xmlns: true, fragment: false });
  const stack: XmlNode[] = [];
  let root: XmlNode | undefined;
  parser.on('doctype', () => { throw new XmlFault('DOCX_DTD_FORBIDDEN'); });
  parser.on('error', () => { throw new XmlFault('DOCX_XML_INVALID', `${parser.line}:${parser.column}`); });
  parser.on('opentag', (tag) => {
    const node: XmlNode = { name: tag.name, local: tag.local, uri: tag.uri, attributes: {}, children: [], text: '' };
    for (const attribute of Object.values(tag.attributes)) {
      node.attributes[`{${attribute.uri}}${attribute.local}`] = attribute.value;
    }
    const parent = stack.at(-1);
    if (parent) parent.children.push(node);
    else root = node;
    stack.push(node);
  });
  parser.on('text', (text) => { const node = stack.at(-1); if (node) node.text += text; });
  parser.on('cdata', (text) => { const node = stack.at(-1); if (node) node.text += text; });
  parser.on('closetag', () => { stack.pop(); });
  parser.write(xml).close();
  if (!root || stack.length) throw new XmlFault('DOCX_XML_INVALID');
  return root;
}

export function* descendants(root: XmlNode): Generator<XmlNode> {
  const pending = [root];
  while (pending.length) {
    const node = pending.pop();
    if (!node) continue;
    yield node;
    for (let index = node.children.length - 1; index >= 0; index--) {
      const child = node.children[index];
      if (child) pending.push(child);
    }
  }
}

export function isWord(node: XmlNode, local: string): boolean {
  return node.local === local && (node.uri === WORD_NS || node.uri === STRICT_WORD_NS);
}
